import { Modal } from '@insify/design-system/components';
import { headingSm, textMd, textMdSemibold } from '@insify/design-system/typography';
import { FunnelAssistant, RichContent, useCoveragesForm } from '@insify/funnel-components';
import { TrackedInputCheckbox } from '@objectiv/tracker-react';
import clsx from 'clsx';
import { forwardRef, useState } from 'react';
import { Minus, Plus } from 'react-feather';
import { useTranslation } from 'react-i18next';

import styles from './CarInsurance.module.css';
import Check from './assets/check.svg';
import LittleCar from './assets/little_car.svg';

export const CarInsurance: React.FC = () => {
  const { t } = useTranslation(['funnel']);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    onToggle,
    coveragesFormMethods: { register },
  } = useCoveragesForm();

  return (
    <>
      <div className={styles.container}>
        <LittleCar className={styles.car} />
        <h2 className={clsx(headingSm, styles.heading)}>{t('funnel:coverage_ghost_card_car_insurance_heading')}</h2>
        <p className={clsx(textMd, styles.description)}>{t('funnel:coverage_ghost_card_car_insurance_description')}</p>
        <Toggle
          {...register('carInsurance', {
            onChange: ({ target }) => {
              onToggle({ type: 'carInsurance', enabled: target.checked });
              if (target.checked) setIsModalOpen(true);
            },
          })}
          className={styles.toggle}
          label={t('funnel:coverage_ghost_card_car_insurance_cta_label')}
          id='carInsurance'
        />
        <div className={styles.details}>
          <div className={styles.iconWrapper}>
            <Check className={styles.check} />
          </div>
          <p className={textMd}>{t('funnel:coverage_ghost_card_car_insurance_details')}</p>
        </div>
      </div>
      <CarInsuranceModal open={isModalOpen} onClose={() => setIsModalOpen(false)} trackingId='car-insurance-modal' />
    </>
  );
};

const Toggle = forwardRef<
  HTMLInputElement,
  React.ComponentProps<typeof TrackedInputCheckbox> & {
    label: string;
  }
>(({ label, id, className, ...props }, ref) => (
  <>
    <TrackedInputCheckbox {...props} ref={ref} hidden id={id} objectiv={{ id, trackValue: true }} role='switch' />
    <label className={clsx(styles.toggle, textMdSemibold, className)} htmlFor={id}>
      <Plus className={styles.plus} />
      <Minus className={styles.minus} />
      {label}
    </label>
  </>
));

const CarInsuranceModal: React.FC<Omit<React.ComponentProps<typeof Modal>, 'children'>> = (props) => {
  const { t } = useTranslation(['funnel']);

  return (
    <Modal {...props}>
      <RichContent.H2 className={styles.modalHeading}>
        {t('funnel:coverage_ghost_card_car_insurance_modal_heading')}
      </RichContent.H2>
      <FunnelAssistant assistant='Tom'>{t('funnel:coverage_ghost_card_car_insurance_modal_assistant')}</FunnelAssistant>
      <RichContent.P className={styles.modalDescription}>
        {t('funnel:coverage_ghost_card_car_insurance_modal_list_intro')}
      </RichContent.P>
      <ul className={styles.modalList}>
        {[
          t('funnel:coverage_ghost_card_car_insurance_modal_list_item_1'),
          t('funnel:coverage_ghost_card_car_insurance_modal_list_item_2'),
          t('funnel:coverage_ghost_card_car_insurance_modal_list_item_3'),
        ].map((item) => (
          <li key={item} className={clsx(textMd, styles.modalListItem)}>
            <div className={styles.iconWrapper}>
              <Check className={styles.modalCheck} />
            </div>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </Modal>
  );
};
